import { useTranslation } from "react-i18next";
import { useAdminAlertsUnread } from "@/features/admin/hooks/useAdminAlertsUnread";
import { cn } from "@/lib/cn";

interface Props {
  /**
   * i18n key for the accessible badge label (interpolated with `count`), e.g.
   * `"adminNav.alertsBadge"` or `"dealerNav.alertsBadge"`, so the same badge
   * serves both shells with the correct panel vocabulary.
   */
  labelKey: string;
  /** Whether the sidebar is collapsed — changes the badge positioning. */
  collapsed: boolean;
}

/**
 * Unread-alert count badge for the Alerts sidebar item (Sprint 142, TASK-4222).
 *
 * Hidden at 0, clamps `count > 9` to `"9+"`. When the sidebar is expanded the
 * badge sits at the end of the row (`ml-auto`); when collapsed it pins to the
 * top-right of the icon like a notification dot. Reuses the shared unread query
 * ({@link useAdminAlertsUnread}) so it updates the moment alerts are marked read.
 */
export function AdminNavAlertsBadge({ labelKey, collapsed }: Props) {
  const { t } = useTranslation();
  const unread = useAdminAlertsUnread();

  if (unread <= 0) return null;

  const display = unread > 9 ? "9+" : String(unread);

  return (
    <span
      aria-label={t(labelKey, { count: unread })}
      className={cn(
        "flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-primary px-1 text-[10px] font-semibold leading-none text-white",
        collapsed ? "absolute right-1 top-1" : "ml-auto",
      )}
    >
      {display}
    </span>
  );
}
